'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  labelBM: string;
  icon: string;
  href: string;
  badge?: number;
  badgeColor?: string;
}

const primaryNav: NavItem[] = [
  { label: 'Home', labelBM: 'Utama', icon: 'HomeIcon', href: '/home' },
  { label: 'My Workspace', labelBM: 'Ruang Kerja Saya', icon: 'RectangleStackIcon', href: '/' },
  { label: 'Interactions', labelBM: 'Interaksi', icon: 'ChatBubbleLeftRightIcon', href: '/interactions', badge: 4, badgeColor: 'bg-blue-500' },
  { label: 'Customer 360', labelBM: 'Pelanggan 360', icon: 'UserCircleIcon', href: '/customer-360' },
  { label: 'Cases', labelBM: 'Kes', icon: 'ClipboardDocumentListIcon', href: '/cases', badge: 7, badgeColor: 'bg-amber-500' },
  { label: 'Knowledge Base', labelBM: 'Pangkalan Pengetahuan', icon: 'BookOpenIcon', href: '/knowledge-base' },
  { label: 'Agencies', labelBM: 'Agensi', icon: 'BuildingOffice2Icon', href: '/agencies' },
  { label: 'Tasks', labelBM: 'Tugasan', icon: 'CheckCircleIcon', href: '/tasks', badge: 3, badgeColor: 'bg-orange-500' },
  { label: 'Analytics', labelBM: 'Analitik', icon: 'ChartBarIcon', href: '/analytics-dashboard' },
];

const bottomNav: NavItem[] = [
  { label: 'Administration', labelBM: 'Pentadbiran', icon: 'Cog8ToothIcon', href: '/administration' },
  { label: 'Settings', labelBM: 'Tetapan', icon: 'AdjustmentsHorizontalIcon', href: '/settings' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeRoute?: string;
  language?: 'EN' | 'BM';
  onNavClick?: () => void;
}

export default function Sidebar({ collapsed, onToggle, activeRoute, language = 'EN', onNavClick }: SidebarProps) {
  const pathname = usePathname();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/' || pathname === '/my-workspace';
    return pathname === href || pathname.startsWith(href + '/');
  };

  const getLabel = (item: NavItem) => language === 'BM' ? item.labelBM : item.label;

  return (
    <aside
      className={`flex flex-col nav-bg h-full transition-all duration-300 ease-in-out flex-shrink-0 relative`}
      style={{ width: collapsed ? 56 : 220, zIndex: 40 }}
    >
      {/* Logo */}
      <div className={`flex items-center gap-2.5 px-3 py-3 border-b border-white/8 min-h-[54px] flex-shrink-0`}>
        {!collapsed ? (
          <>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* White logo on dark background */}
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-white/15">
                <AppLogo size={26} className="flex-shrink-0" variant="dark-bg" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-700 text-[14px] leading-tight tracking-tight">MyGCC</div>
                <div className="text-[10px] leading-tight font-500" style={{ color: 'rgba(184,204,228,0.7)' }}>CX CRM</div>
              </div>
            </div>
            <button
              onClick={onToggle}
              className="p-1 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
              title="Collapse sidebar"
            >
              <Icon name="ChevronLeftIcon" size={13} className="text-nav-fg opacity-60" />
            </button>
          </>
        ) : (
          <button
            onClick={onToggle}
            className="mx-auto p-1.5 rounded-md hover:bg-white/10 transition-colors"
            title="Expand sidebar"
          >
            <div className="w-6 h-6 rounded flex items-center justify-center overflow-hidden bg-white/15">
              <AppLogo size={18} className="flex-shrink-0" variant="dark-bg" />
            </div>
          </button>
        )}
      </div>

      {/* Primary Nav */}
      <nav className="flex-1 py-2 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <div className="nav-section-label mt-1 mb-1">{language === 'BM' ? 'Operasi' : 'Operations'}</div>
        )}
        {primaryNav.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={`nav-${item.label}`}
              href={item.href}
              onClick={onNavClick}
              title={collapsed ? getLabel(item) : undefined}
              className={`relative flex items-center gap-2.5 mx-1.5 my-0.5 px-2.5 py-2 rounded-lg text-[13px] font-500 transition-all duration-150 group
                ${active ? 'nav-item-active text-white' : 'text-nav-fg nav-item-hover'}`}
            >
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={15} className="flex-shrink-0" />
              {!collapsed && (
                <span className="truncate flex-1">{getLabel(item)}</span>
              )}
              {!collapsed && item.badge !== undefined && (
                <span className={`${item.badgeColor || 'bg-primary'} text-white text-[10px] font-700 rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center notification-badge`}>
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge !== undefined && (
                <span className={`absolute top-0.5 right-0.5 ${item.badgeColor || 'bg-primary'} text-white text-[9px] font-700 rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none`}>
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Nav */}
      <div className="border-t py-2 flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        {!collapsed && (
          <div className="nav-section-label mb-1">{language === 'BM' ? 'Sistem' : 'System'}</div>
        )}
        {bottomNav.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={`nav-bottom-${item.label}`}
              href={item.href}
              onClick={onNavClick}
              title={collapsed ? getLabel(item) : undefined}
              className={`flex items-center gap-2.5 mx-1.5 my-0.5 px-2.5 py-2 rounded-lg text-[13px] font-500 transition-all duration-150
                ${active ? 'nav-item-active text-white' : 'text-nav-fg nav-item-hover'}`}
            >
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={15} className="flex-shrink-0" />
              {!collapsed && <span className="truncate">{getLabel(item)}</span>}
            </Link>
          );
        })}

        {/* Agent profile */}
        <div className="relative mx-1.5 mt-1">
          <button
            onClick={() => setUserMenuOpen(v => !v)}
            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border hover:bg-white/8 transition-colors ${collapsed ? 'justify-center' : ''}`}
            style={{ borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-700 flex-shrink-0">
              NA
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-white text-[11px] font-600 truncate">Nur Aisyah</div>
                  <div className="text-[9px] opacity-50 truncate" style={{ color: '#b8cce4' }}>CS Officer · MyGCC</div>
                </div>
                <Icon name="ChevronUpDownIcon" size={11} className="text-nav-fg opacity-50 flex-shrink-0" />
              </>
            )}
          </button>

          {userMenuOpen && !collapsed && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-border rounded-xl shadow-elevated py-1 z-50 slide-in-up">
              {[
                { label: 'My Profile', icon: 'UserIcon', href: '/settings' },
                { label: 'Agent Status', icon: 'SignalIcon', href: '/settings' },
                { label: 'Security', icon: 'ShieldCheckIcon', href: '/settings' },
              ].map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2 text-[12px] text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={13} className="text-muted-foreground" />
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-border mt-1 pt-1">
                <Link
                  href="/login"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2 text-[12px] text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={13} />
                  Sign Out
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}